// Sprint PKG-VIZ A1 (#792) — radial concentric-ring layout helper.
//
// Centre node at (0,0); 1-hop ring at radius 200, 2-hop ring at radius
// 400, etc. Hop distance is computed via BFS over edges treated as
// undirected (a knowledge-graph "how far is this from the centre"
// question doesn't care about edge direction). Deterministic: BFS
// visits neighbours in a fixed (sorted-id) order, and nodes within a
// ring are angularly distributed in sorted-id order — same [nodes,
// edges] input always produces the same positions. Required by S3 /
// adjudication Q6.

import type { PKGNode, PKGEdge } from '@blueprint/shared';
import type { PKGLayoutPositions } from './types.ts';

const RING_RADIUS_STEP = 200;

function buildAdjacency(nodes: PKGNode[], edges: PKGEdge[]): Map<string, string[]> {
  const adjacency = new Map<string, string[]>();
  for (const node of nodes) adjacency.set(node.id, []);
  for (const edge of edges) {
    if (adjacency.has(edge.source) && adjacency.has(edge.target)) {
      adjacency.get(edge.source)!.push(edge.target);
      adjacency.get(edge.target)!.push(edge.source);
    }
  }
  // Sort each adjacency list so BFS traversal order is deterministic
  // regardless of the input edges' array order.
  for (const neighbours of adjacency.values()) neighbours.sort();
  return adjacency;
}

function computeHopDistances(centerNodeId: string, adjacency: Map<string, string[]>): Map<string, number> {
  const hops = new Map<string, number>([[centerNodeId, 0]]);
  const queue: string[] = [centerNodeId];
  let head = 0;
  while (head < queue.length) {
    const current = queue[head++];
    const currentHop = hops.get(current)!;
    for (const neighbour of adjacency.get(current) ?? []) {
      if (!hops.has(neighbour)) {
        hops.set(neighbour, currentHop + 1);
        queue.push(neighbour);
      }
    }
  }
  return hops;
}

export function computeRadialLayout(
  nodes: PKGNode[],
  edges: PKGEdge[],
  centerNodeId?: string,
): PKGLayoutPositions {
  const positions: PKGLayoutPositions = new Map();
  if (nodes.length === 0) return positions;

  // Deterministic fallback centre: first node in sorted-id order, not
  // array order, so re-ordering the same node set doesn't change layout.
  const sortedIds = [...nodes].map(n => n.id).sort();
  const center = centerNodeId ?? sortedIds[0];

  const adjacency = buildAdjacency(nodes, edges);
  const hops = computeHopDistances(center, adjacency);

  // R2 fix (F7): Math.max(0, ...spread) blows the engine's argument-count
  // ceiling on large graphs (a plausible input for the partner-portfolio
  // aggregation surface this contract serves) — a plain loop has no such
  // limit.
  let maxHop = 0;
  for (const hop of hops.values()) {
    if (hop > maxHop) maxHop = hop;
  }
  const unreachableHop = maxHop + 1;

  // Group node ids by ring (hop distance), sorted-id order within each ring.
  const rings = new Map<number, string[]>();
  for (const id of sortedIds) {
    const hop = hops.get(id) ?? unreachableHop;
    if (!rings.has(hop)) rings.set(hop, []);
    rings.get(hop)!.push(id);
  }

  for (const [hop, ringNodeIds] of rings) {
    const radius = hop * RING_RADIUS_STEP;
    if (hop === 0) {
      positions.set(ringNodeIds[0], { x: 0, y: 0 });
      continue;
    }
    const angleStep = (2 * Math.PI) / ringNodeIds.length;
    ringNodeIds.forEach((id, index) => {
      const angle = index * angleStep;
      positions.set(id, {
        x: radius * Math.cos(angle),
        y: radius * Math.sin(angle),
      });
    });
  }

  return positions;
}
